import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  TextField,
  LinearProgress,
  Alert,
  IconButton,
  CssBaseline,
} from "@mui/material";
import { CheckCircle } from "@mui/icons-material";
import axios from "axios";
import { motion } from "framer-motion";
import CreditsBanner from "../Components/CreditsNudge";
import { message } from "../Constants/message.js";
import {
  ContentBox,
  StyledButton,
  TitleText,
  SubtitleText,
  PageContainer,
} from "./Default.js";
import { Link } from "react-router-dom";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import EmailAliasGeneratorContent from "../Contents/EmailAliasGeneratorContent.js";
import CommentSection from "../Components/CommentSection.js";

function EmailAliasGenerator({ user, login, isLoggedIn }) {
  const [realEmail, setRealEmail] = useState("");
  const [aliasPrefix, setAliasPrefix] = useState("");
  const [alias, setAlias] = useState(""); // Storing only one alias
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [credits, setCredits] = useState({
    remaining: 0,
    availed: 0,
    total: 0,
    loading: true,
  });
  useEffect(() => {
    const primaryInput = localStorage.getItem("primaryInputText");
    if (primaryInput) {
      setRealEmail(primaryInput);
    }
    const secondaryInput = localStorage.getItem("secondaryInputText");
    if (secondaryInput) {
      setAliasPrefix(secondaryInput);
    }
  }, []);
  const [domainError, setDomainError] = useState(""); // To track domain errors

  // List of supported domains for plus-addressing aliasing
  const plusAddressingSupported = [
    "gmail.com",
    "outlook.com",
    "hotmail.com",
    "live.com",
    "msn.com",
    "protonmail.com",
    "zoho.com",
    "fastmail.com",
    "tutanota.com",
    "hey.com",
    "mailfence.com",
    "yandex.com",
    "gmx.com",
  ];

  // List of random words to be used as alias when the prefix is empty
  const randomWords = [
    "shopping",
    "work",
    "social",
    "download",
    "newsletter",
    "promotion",
    "offers",
    "alerts",
    "transactions",
    "updates",
  ];

  const getRandomAlias = () => {
    const randomIndex = Math.floor(Math.random() * randomWords.length);
    return randomWords[randomIndex];
  };

  const handleGenerateAlias = async () => {
    setSuccess("");
    setError("");
    setDomainError("");

    const currentPath = window.location.pathname.replace(/^\/+|\/+$/g, "");
    const toolKey = currentPath.replace(/[\/]/g, "_") || "unknown_tool";
    const guestUsedKey = `${toolKey}_guestUsed`;

    // Guest one-time access
    if (!isLoggedIn) {
      const hasUsedGuestAccess = localStorage.getItem(guestUsedKey);
      if (hasUsedGuestAccess) {
        setError(message.loginPrompt);
        setTimeout(() => {
          login();
        }, 1500);
        return;
      } else {
        localStorage.setItem(guestUsedKey, "true");
      }
    }

    if (isLoggedIn && user.account_type.toLowerCase() === "free") {
      if (credits.remaining < 1) {
        setError(
          "Insufficient credits to generate emails. Please upgrade your plan."
        );
        return;
      }
    }

    if (!realEmail.trim()) {
      setError("Please enter a real email address.");
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(realEmail.trim())) {
      setError("Please enter a valid email format.");
      return;
    }

    const domain = realEmail.trim().split("@")[1].toLowerCase();
    if (!plusAddressingSupported.includes(domain)) {
      setDomainError(
        `The domain "${domain}" does not support aliasing with "+". Please use a supported provider like Gmail or Outlook.`
      );
      return;
    }

    setLoading(true);

    const sanitizedAliasPrefix = aliasPrefix.trim().replace(/\s+/g, "_");
    const prefix = sanitizedAliasPrefix || getRandomAlias();
    const emailUsername = realEmail.split("@")[0];
    const generatedAlias = `${emailUsername}+${prefix}@${
      realEmail.split("@")[1]
    }`;

    setLoading(false);
    setAlias(generatedAlias);
    setSuccess("Alias generated successfully!");
    setAliasPrefix("");

    if (isLoggedIn && user.account_type.toLowerCase() === "free") {
      const creditsResponse = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/credit/updateCredits`,
        { toolName: "email_alias_generator" }
      );
      setCredits({
        remaining: creditsResponse.data.remaining_credits || 0,
        availed: creditsResponse.data.availed_credits || 0,
        total: creditsResponse.data.total_credits || 0,
        loading: false,
      });
    } else if (isLoggedIn && user.account_type.toLowerCase() === "paid") {
      await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/credit/updateUsage`,
        { toolName: "email_alias_generator" }
      );
    }
  };

  const handleCopyAlias = () => {
    navigator.clipboard.writeText(alias); // Copy alias to clipboard
    setSuccess("Alias copied to clipboard!");
  };

  return (
    <>
      {isLoggedIn && (
        <CreditsBanner credits={credits} setCredits={setCredits} />
      )}
      <CssBaseline />
      <PageContainer sx={{ paddingTop: 2 }}>
        <ContentBox>
          <TitleText variant="h3" align="center">
            Email Alias Generator 📬
          </TitleText>

          <SubtitleText variant="subtitle1" align="center">
            Generate a custom email alias that forwards to your real email
          </SubtitleText>

          <Box mb={3} sx={{ maxWidth: 400, mx: "auto" }}>
            <TextField
              fullWidth
              label="Real Email Address"
              variant="outlined"
              value={realEmail}
              onChange={(e) => setRealEmail(e.target.value)}
              placeholder="e.g. user@example.com"
              size="medium"
              name="primary-textbox"
            />
          </Box>

          <Box mb={3} sx={{ maxWidth: 400, mx: "auto" }}>
            <TextField
              fullWidth
              label="Alias Prefix (optional)"
              variant="outlined"
              value={aliasPrefix}
              onChange={(e) => setAliasPrefix(e.target.value)}
              placeholder="e.g. shopping, work, social, download, etc."
              size="medium"
              name="secondary-textbox"
            />
          </Box>

          {loading && <LinearProgress sx={{ mb: 2 }} />}

          {error && (
            <Alert severity="error" sx={{ mb: 2, maxWidth: 400, mx: "auto" }}>
              {error}
            </Alert>
          )}
          {success && (
            <Alert severity="success" sx={{ mb: 2, maxWidth: 400, mx: "auto" }}>
              {success}
            </Alert>
          )}

          <Box display="flex" justifyContent="center" gap={2}>
            <StyledButton
              variant="contained"
              color="primary"
              onClick={handleGenerateAlias}
            >
              Generate Alias
            </StyledButton>
          </Box>

          {alias && (
            <Box mt={4}>
              <Typography variant="h5" textAlign="center" mb={2}>
                Your Generated Alias
              </Typography>
              <Box sx={{ maxWidth: 500, mx: "auto" }}>
                <Box
                  sx={{
                    p: 2,
                    border: "1px solid #ddd",
                    borderRadius: "12px",
                    mb: 2,
                    backgroundColor: "#fafafa",
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <Typography variant="body1" fontWeight="bold">
                    {alias}
                  </Typography>
                  <IconButton onClick={handleCopyAlias}>
                    <ContentCopyIcon />
                  </IconButton>
                </Box>
              </Box>
            </Box>
          )}

          {domainError && (
            <Alert severity="error" sx={{ mb: 2, maxWidth: 400, mx: "auto" }}>
              {domainError}
            </Alert>
          )}

          {isLoggedIn && user.account_type.toLowerCase() === "free" ? (
            <Typography
              variant="body2"
              color="text.secondary"
              align="center"
              mt={2}
            >
              <b>⚠️ Free Plan Limitations:</b> For Free plan users, 1 credit
              will be deducted for each alias email generation.{" "}
              <b>All Paid plan users</b> enjoy this tool for free 🎁{" "}
              <b>without any credit deduction</b>.
            </Typography>
          ) : (
            isLoggedIn && (
              <Typography
                variant="body2"
                color="text.secondary"
                align="center"
                mt={2}
              >
                <b>
                  🎁 Good news! For all Paid plan users, alias email generation
                  is completely complimentary — no credits will be deducted.
                </b>
              </Typography>
            )
          )}
          {/* Updated Supported Email Domains Section */}
          <Box
            mt={4} // Increased margin-top for spacing
            sx={{
              textAlign: "center",
              padding: "2rem", // Added padding to the container
              borderRadius: "1rem", // Added rounded corners
              backgroundColor: "rgba(255, 255, 255, 0.8)", // Semi-transparent white background
              boxShadow: "0 6px 18px rgba(0, 0, 0, 0.15)", // More pronounced shadow
              backdropFilter: "blur(12px)", // Added blur effect
              border: "1px solid rgba(255, 255, 255, 0.3)", // Added a border
            }}
          >
            <Typography
              variant="h6"
              gutterBottom
              sx={{
                fontWeight: "600", // Made the title bold
                color: "#2d3748", // Darker text color
                marginBottom: "1.5rem",
                fontSize: "1.5rem",
              }}
            >
              <CheckCircle className="inline-block w-6 h-6 mr-2 text-green-500" />
              Supported Email Domains
            </Typography>
            <Box
              component="ul"
              sx={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", // Increased min width
                gap: 2, // Increased gap
                justifyContent: "center",
                marginTop: "1.5rem",
                padding: 0,
              }}
            >
              {plusAddressingSupported.map((domain) => (
                <motion.li
                  key={domain}
                  style={{ listStyle: "none", textAlign: "center" }}
                  whileHover={{ scale: 1.1 }} // Added scale on hover
                  transition={{ type: "spring", stiffness: 300 }}
                >
                  <Typography
                    variant="body1" // Use body1 for larger text
                    sx={{
                      color: "#4a5568", // Slightly darker for better readability
                      padding: "0.5rem 1rem", // Added padding
                      borderRadius: "0.75rem", // Added rounded corners to each item
                      backgroundColor: "rgba(247, 250, 252, 0.8)", // Very light background
                      boxShadow: "0 2px 4px rgba(0, 0, 0, 0.08)", // Subtle shadow
                      transition:
                        "background-color 0.3s ease, transform 0.2s ease", // Smooth transition
                      ":hover": {
                        backgroundColor: "rgba(235, 245, 255, 0.95)", // Slightly darker on hover
                      },
                      border: "1px solid rgba(255,255,255,0.1)",
                    }}
                  >
                    {domain}
                  </Typography>
                </motion.li>
              ))}
            </Box>
          </Box>

          <Box mt={3}>
            <Typography variant="body2" color="text.secondary" align="center">
              Need help? <Link to="/contact">Contact support</Link>
            </Typography>
          </Box>
        </ContentBox>
        <CommentSection
          toolId="email-alias-generator"
          user={user}
          login={login}
          isLoggedIn={isLoggedIn}
        />
        <EmailAliasGeneratorContent />
      </PageContainer>
    </>
  );
}

export default EmailAliasGenerator;
